import { useEffect, useState } from 'react';
import { supabase } from '../utils/supabase';
import { useUser } from './useUser';
import { PHOTO_CHANGED_EVENT } from '../components/ut/profile/photo';

interface NavViewer {
  playerId: string | null;
  friendlyName: string | null;
  initial: string;
  xp: number;
  streak: number;
  rank: number | null;
  rarity: string | null;
  /** Uploaded photo URL, or null. */
  photo: string | null;
  loading: boolean;
}

export const useNavViewer = (): NavViewer => {
  const { player, loading: userLoading } = useUser();
  const [xp, setXp] = useState(0);
  const [streak, setStreak] = useState(0);
  const [rank, setRank] = useState<number | null>(null);
  const [rarity, setRarity] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [photo, setPhoto] = useState<string | null>(null);

  // Kept separate so it can refresh on its own after an upload.
  useEffect(() => {
    if (!player?.id) {
      setPhoto(null);
      return;
    }
    let cancelled = false;
    const load = () =>
      supabase
        .from('players')
        .select('photo_url')
        .eq('id', player.id)
        .maybeSingle()
        .then(({ data }) => {
          if (!cancelled) setPhoto((data?.photo_url as string | null) ?? null);
        });
    void load();
    window.addEventListener(PHOTO_CHANGED_EVENT, load);
    return () => {
      cancelled = true;
      window.removeEventListener(PHOTO_CHANGED_EVENT, load);
    };
  }, [player?.id]);

  useEffect(() => {
    let cancelled = false;
    if (!player?.id) {
      setXp(0);
      setStreak(0);
      setRank(null);
      setRarity(null);
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      try {
        // Per data-shape pitfall: useStats().comprehensiveStats hardcodes xp:0 —
        // query player_xp directly.
        const { data: xpRow } = await supabase
          .from('player_xp')
          .select('xp, rank, rarity')
          .eq('player_id', player.id)
          .maybeSingle();

        if (!cancelled) {
          setXp(xpRow?.xp ?? 0);
          setRank(xpRow?.rank ?? null);
          setRarity(xpRow?.rarity ?? null);
        }

        // current_streak on the players row is the live value and is what
        // this nav badge wants. (players.max_streak used to be stale too, but
        // is maintained as of Sep 2026 -- player_streak_stats.longest_streak
        // remains the wrong source, since it counts reserves and dropouts.)
        if (!cancelled) {
          setStreak(player.current_streak ?? 0);
        }
      } catch (err) {
        console.error('useNavViewer load error', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [player?.id, player?.current_streak]);

  return {
    playerId: player?.id ?? null,
    friendlyName: player?.friendly_name ?? null,
    initial: player?.friendly_name?.charAt(0).toUpperCase() ?? '?',
    xp,
    streak,
    rank,
    rarity,
    photo,
    loading: userLoading || loading,
  };
};
